import React, { Component } from 'react';
import styled from 'styled-components/macro';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import scaleSize from '../../utils/scale-size';
import BackButton from '../BackButton';
import List from './List';
import PlayerSettingsList from './PlayerSettingsList';
import { findKey, map, isEmpty } from 'lodash';
import i18n from 'i18n';

export const StyledBlock = styled.div`
  width: ${scaleSize(1480)}px;
  font-family: Roboto, sans-serif;
  font-size: ${scaleSize(34)}px;
  line-height: 1.41;
  margin: ${scaleSize(40)}px auto;
  color: ${({ theme }) => theme.mainTextColor};
`;

export const StyledContent = styled.div`
  transform: translate3d(0, ${({ top }) => top || 0}px, 0);
  margin-bottom: ${scaleSize(94)}px;
`;

const ONE_SCROLL_STEP = scaleSize(1080);

const StyledOverlay = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.backgroundColor};
  z-index: 101;
`;

const ListTitle = styled.div`
  width: ${scaleSize(1800)}px;
  height: ${scaleSize(72)}px;
  font-family: 'RobotoRegular', sans-serif;
  font-size: ${scaleSize(48)}px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  text-align: center;
  margin: ${scaleSize(50)}px auto;
  color: ${({ theme }) => theme.defaultColor};
`;

const PlayerSettingsTitle = styled(ListTitle)`
  text-align: left;
  padding-left: ${scaleSize(210)}px;
`;

const PlayerSettingsWrapper = styled.div`
  position: absolute;
  top: ${scaleSize(260)}px;
  left: ${scaleSize(180)}px;
`;

const PlayerSettingsSection = styled.div`
  width: ${scaleSize(550)}px;
  display: inline-block;
  vertical-align: top;
`;

const PlayerSettingsListTitle = styled.div`
  opacity: 0.75;
  font-size: ${scaleSize(40)}px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: normal;
  color: ${({ theme }) => theme.defaultColor};
  margin-bottom: ${scaleSize(50)}px;
  padding-left: ${scaleSize(40)}px;
`;

const TextInfo = styled.div`
  width: ${scaleSize(1480)}px;
  height: auto;
  padding: ${scaleSize(100)}px 0;
  font-family: 'RobotoRegular', sans-serif;
  font-size: ${scaleSize(34)}px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  text-align: center;

  color: ${({ theme }) => theme.defaultColor};
`;

class Overlay extends Component {
  contentBlockRef = React.createRef();

  state = {
    contentTopOffset: 0,
  };

  componentDidMount() {
    document.addEventListener('keydown', this.onKeydown, true);
    document.addEventListener('wheel', this.onWheel, true);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeydown, true);
    document.removeEventListener('wheel', this.onWheel, true);
  }
  onKeydown = ({ keyCode }) => {
    const { downKeyCode, upKeyCode } = this.props;
    if (![downKeyCode, upKeyCode].includes(keyCode)) return;

    this.setOffsetPostions(keyCode === downKeyCode ? -ONE_SCROLL_STEP : ONE_SCROLL_STEP);
  };

  onWheel = ({ deltaY }) => this.setOffsetPostions(deltaY > 0 ? -ONE_SCROLL_STEP : ONE_SCROLL_STEP);

  //hidden scroll for long descriptions
  setOffsetPostions = step => {
    if (!this.contentBlockRef.current) return;

    const contentBlockHeight = this.contentBlockRef.current.offsetHeight;

    if (contentBlockHeight < ONE_SCROLL_STEP) return;

    const { contentTopOffset: oldContentTopOffset } = this.state;
    let contentTopOffset = oldContentTopOffset + step;
    // проверка на overflow вверху или внизу
    if (Math.abs(contentTopOffset) + ONE_SCROLL_STEP > contentBlockHeight) {
      contentTopOffset = -contentBlockHeight + ONE_SCROLL_STEP;
    } else if (contentTopOffset > 0) {
      contentTopOffset = 0;
    }

    this.setState({
      contentTopOffset,
    });
  };
  renderList() {
    const { options } = this.props;

    return (
      <>
        <ListTitle>{options.listTitle}</ListTitle>
        <List
          sectionName={'OVERLAY_CONTENTS'}
          items={options.items}
          selectedItem={options.selected}
          onSelect={options.onSelect}
          closeAndReturnFocus={this.closeAndReturnFocus}
        />
      </>
    );
  }
  renderText() {
    const { options } = this.props;
    const { contentTopOffset } = this.state;

    return (
      <StyledBlock>
        <StyledContent top={contentTopOffset} ref={this.contentBlockRef}>
          <TextInfo dangerouslySetInnerHTML={{ __html: options.fullDescription }} />
        </StyledContent>
      </StyledBlock>
    );
  }
  renderPlayerSettings() {
    const { options } = this.props;
    let selectedLangId, selectedQualityId;

    const qualityOptions = map(options.quality, ({ id, isActive, name }) => {
      if (isActive) selectedQualityId = id;
      const bandwidthForTranslations = this.transformBandwidthToMbit(id);
      const qualityLabel = `player.videoQualities.${bandwidthForTranslations}`;
      return {
        id,
        title: name || i18n.t(qualityLabel),
        type: 'videoQuality',
      };
    });

    const langOptions = map(options.language, ({ id, name, isActive }) => {
      if (isActive) selectedLangId = id;
      return {
        id,
        title: name || i18n.t(`player.audioTracks.${id}`, id),
        type: 'audioTrack',
      };
    });

    return (
      <>
        <PlayerSettingsTitle>{i18n.t('player.settings.title')}</PlayerSettingsTitle>
        <PlayerSettingsWrapper>
          {!isEmpty(qualityOptions) && (
            <PlayerSettingsSection>
              <PlayerSettingsListTitle>
                {i18n.t('player.settings.qualityTitle')}
              </PlayerSettingsListTitle>
              <PlayerSettingsList
                sectionName={'OVERLAY_CONTENTS'}
                items={qualityOptions}
                selectedItem={selectedQualityId}
                onSelect={options.onSelect}
                closeAndReturnFocus={this.closeAndReturnFocus}
              />
            </PlayerSettingsSection>
          )}
          {!isEmpty(langOptions) ? (
            <PlayerSettingsSection>
              <PlayerSettingsListTitle>
                {i18n.t('player.settings.audioTitle')}
              </PlayerSettingsListTitle>
              <PlayerSettingsList
                sectionName={'OVERLAY_CONTENTS_RIGHT'}
                items={langOptions}
                selectedItem={selectedLangId}
                onSelect={options.onSelect}
                closeAndReturnFocus={this.closeAndReturnFocus}
              />
            </PlayerSettingsSection>
          ) : null}
        </PlayerSettingsWrapper>
      </>
    );
  }
  renderContents() {
    const { type } = this.props;

    switch (type) {
      case 'list':
        return this.renderList();
      case 'text':
        return this.renderText();
      case 'player-settings':
        return this.renderPlayerSettings();
      default:
        console.warn('unknown overlay type');
        return;
    }
  }
  closeAndReturnFocus = () => {
    const { hideOverlay, returnFocusTo, options } = this.props;
    SpatialNavigationWrapper.focus(returnFocusTo);
    if (typeof options.onClose === 'function') options.onClose();
    hideOverlay();
  };
  transformBandwidthToMbit = bandwidth => {
    const divider = 1000000;
    const videoBandwidth = (bandwidth / divider).toFixed(1);

    const bandwidthRanges = {
      minimal: 1.1,
      low: 1.7,
      medium: 2.5,
      high: 3.6,
      maximal: 999,
    };

    return findKey(bandwidthRanges, range => videoBandwidth <= range);
  };
  render() {
    const { show, type } = this.props;

    if (!show) return null;

    const downNavigateSection = type === 'text' ? '' : '@OVERLAY_CONTENTS';

    return (
      <StyledOverlay>
        <BackButton
          defaultSection={type === 'text'}
          onEnter={this.closeAndReturnFocus}
          sectionName={'OVERLAY_BACK_BUTTON'}
          navigationConfig={{
            leaveFor: { up: '', left: '', right: '', down: downNavigateSection },
          }}
        />
        {this.renderContents()}
      </StyledOverlay>
    );
  }
}

export default Overlay;
